/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Occurra Cloud Metadata Service
 * Analytics metadata storage and search service backed by ClickHouse.
 *
 * The service validates every request under `/metadata/api/v1` against this document. A
 * request that does not match gets a 400 with an `Error` body.
 *
 * Each operation names who may call it in `x-access`, and the service registers the operation
 * behind the middleware of that chain:
 * - `public`: anyone.
 * - `authenticated`: a holder of a cloud token, a user's session or a server's machine token.
 *   An operation whose body names the organization, as `organizationId`, has the handler
 *   check that the caller is a member of it.
 * - `org-scoped`: the same caller, a member of the organization in the `orgId` query
 *   parameter, with a role that allows the operation. The answer is narrowed to the cameras
 *   the caller reaches.
 *
 * The service does not start when an operation names no known `x-access`.
 *
 * OpenAPI spec version: 2.1.0
 */
import * as zod from 'zod';

export const Error = zod.object({
  "error": zod.string()
});

export type Error = zod.input<typeof Error>;
export type ErrorOutput = zod.output<typeof Error>;

export const Status = zod.object({
  "status": zod.string()
});

export type Status = zod.input<typeof Status>;
export type StatusOutput = zod.output<typeof Status>;

export const InsertResult = zod.object({
  "inserted": zod.int()
});

export type InsertResult = zod.input<typeof InsertResult>;
export type InsertResultOutput = zod.output<typeof InsertResult>;

export const OrderBy = zod.enum(['similarity', 'startTimeMs']).describe('Result order. `similarity` is the default of a vector search and needs a ranking signal. `startTimeMs` is newest first: the only order of attribute search, and on a vector search a row-budgeted brute-force scan. The order changes only which page of the result set `limit` exposes, never which rows qualify.\n');

export type OrderBy = zod.input<typeof OrderBy>;
export type OrderByOutput = zod.output<typeof OrderBy>;

export const GuidRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');


export const Guid = zod.string().regex(GuidRegExp).describe('An id in the 8-4-4-4-12 hex form. Only the form is checked: media_server ids and the ids that rules derive carry no RFC 9562 version digit.\n');

export type Guid = zod.input<typeof Guid>;
export type GuidOutput = zod.output<typeof Guid>;

export const regionInputTwoItemMin = 0;
export const regionInputTwoItemMax = 255;



export const RegionInput = zod.union([zod.string(),zod.array(zod.int().min(regionInputTwoItemMin).max(regionInputTwoItemMax))]).describe('A 32x56 bitmask (224 bytes): base64, or an array of byte values, which is how media_server sends it.\n');

export type RegionInput = zod.input<typeof RegionInput>;
export type RegionInputOutput = zod.output<typeof RegionInput>;

export const Rect = zod.object({
  "x": zod.number(),
  "y": zod.number(),
  "width": zod.number(),
  "height": zod.number()
});

export type Rect = zod.input<typeof Rect>;
export type RectOutput = zod.output<typeof Rect>;

export const Embedding = zod.object({
  "data": zod.array(zod.number()).describe('Dense float vector, L2-normalized, at the native dim of the producer model. The service truncates a text vector to its storage dim (Matryoshka).\n'),
  "model": zod.string().optional().describe('The producer model, for example `Qwen/Qwen3-Embedding-0.6B`. Empty on a read: the embedding tables do not store it.\n')
});

export type Embedding = zod.input<typeof Embedding>;
export type EmbeddingOutput = zod.output<typeof Embedding>;

export const ImageData = zod.object({
  "imageTimestampUs": zod.int().optional().describe('Microseconds; -1 when the agent had no frame time for it.'),
  "rect": Rect.optional()
}).describe('Where in the archive a best shot is cut. A read never carries the bytes.');

export type ImageData = zod.input<typeof ImageData>;
export type ImageDataOutput = zod.output<typeof ImageData>;

export const ImageInput = zod.object({
  "data": zod.string().optional().describe('Image bytes, base64.'),
  "mimeType": zod.string().optional().describe('`image/jpeg` or `image/png`.'),
  "imageTimestampUs": zod.int().optional(),
  "rect": Rect.optional()
}).describe('An image in a request. Without `data`, `imageTimestampUs` and `rect` address the archive frame of the best shot.\n');

export type ImageInput = zod.input<typeof ImageInput>;
export type ImageInputOutput = zod.output<typeof ImageInput>;

export const EventMetadata = zod.object({
  "id": Guid,
  "organizationId": zod.string(),
  "deviceId": Guid,
  "agentId": zod.string(),
  "category": zod.string(),
  "utcTimeMs": zod.int(),
  "utcEndTimeMs": zod.int(),
  "confidence": zod.number(),
  "trackId": zod.string().optional(),
  "status": zod.string().optional(),
  "parentEventId": Guid.describe('The zero id when the event has no parent.'),
  "rect": Rect.optional().describe('Not stored. The bounding rect of `region`, on the 32x56 grid.'),
  "region": zod.string().optional().describe('The 32x56 detection bitmask (224 bytes), base64: the spatial truth that every region filter matches against.\n'),
  "image": ImageData.optional(),
  "enrollmentId": Guid.optional().describe('The enrolled identity that media_server matched the object to, stored verbatim. Absent when the object matched none. `GET /vms/api/v1/enrollments` resolves it to a name.\n'),
  "enrollmentSimilarity": zod.number().optional().describe('The winning cosine similarity. Present when `enrollmentId` is.'),
  "hasEmbeddedImage": zod.boolean().describe('True when the service stores the image bytes (`GET /events/{id}/images`). When false, `image` addresses the archive frame on the media_server.\n'),
  "attributes": zod.record(zod.string(), zod.string()),
  "textEmbedding": Embedding.optional(),
  "imageEmbedding": Embedding.optional()
}).describe('An event as the service reads it.');

export type EventMetadata = zod.input<typeof EventMetadata>;
export type EventMetadataOutput = zod.output<typeof EventMetadata>;

export const EventSearchResult = EventMetadata.and(zod.object({
  "similarity": zod.number().optional().describe('1 - cosineDistance(query, embedding), from -1 to 1. Present in a similarity mode only.\n')
}).describe('`textEmbedding` / `imageEmbedding` are set only with `withEmbeddings=true`. A text search sets `textEmbedding`, and `imageEmbedding` when the event has a ReID vector; an image search sets `imageEmbedding`.\n'));

export type EventSearchResult = zod.input<typeof EventSearchResult>;
export type EventSearchResultOutput = zod.output<typeof EventSearchResult>;




export const EventInsert = zod.object({
  "id": zod.uuid(),
  "organizationId": zod.string().min(1),
  "deviceId": zod.string().optional(),
  "agentId": zod.string().optional(),
  "category": zod.string().optional(),
  "utcTimeMs": zod.int().optional(),
  "utcEndTimeMs": zod.int().optional(),
  "confidence": zod.number().optional(),
  "trackId": zod.string().optional(),
  "status": zod.string().optional(),
  "parentEventId": zod.uuid().optional(),
  "rect": Rect.optional().describe('Rasterized into the region when the event carries no `region`.'),
  "region": RegionInput.optional(),
  "image": ImageInput.optional().describe('The best shot. With `data`, the service stores the bytes.'),
  "enrollmentId": zod.uuid().optional(),
  "enrollmentSimilarity": zod.number().optional(),
  "attributes": zod.record(zod.string(), zod.string()).optional(),
  "textEmbedding": Embedding.optional(),
  "imageEmbedding": Embedding.optional().describe('The ReID vector. When absent and the service runs with `--enrich-images`, the service computes it from `image`.\n'),
  "embeddingOnly": zod.boolean().optional().describe('Store the event only in the embedding tables, from the agent manifest\'s `embeddingOnly` flag. Reads never return it.\n')
}).describe('An event as media_server uploads it (`CloudEventMetadata`).\n\nAn embedding whose model the vectorization service does not advertise, or whose length\nis not the model\'s dim, is cleared and logged; the record is still stored.\n');

export type EventInsert = zod.input<typeof EventInsert>;
export type EventInsertOutput = zod.output<typeof EventInsert>;

export const EmbeddingHit = zod.object({
  "id": Guid,
  "deviceId": Guid,
  "category": zod.string(),
  "utcTimeMs": zod.int(),
  "utcEndTimeMs": zod.int(),
  "similarity": zod.number().describe('1 - cosineDistance(query, embedding), from -1 to 1.'),
  "textEmbedding": Embedding.optional(),
  "imageEmbedding": Embedding.optional()
}).describe('A row of a vector search over the embedding tables, with no JOIN to `events`. `textEmbedding` / `imageEmbedding` are set only with `withEmbeddings=true`: a text search sets `textEmbedding`, an image search `imageEmbedding`.\n');

export type EmbeddingHit = zod.input<typeof EmbeddingHit>;
export type EmbeddingHitOutput = zod.output<typeof EmbeddingHit>;


export const searchRequestLimitDefault = 100;
export const searchRequestLimitMax = 1000;

export const searchRequestOffsetDefault = 0;
export const searchRequestOffsetMin = 0;

export const searchRequestWithEmbeddingsDefault = false;
export const searchRequestMinSimilarityMin = 0;
export const searchRequestMinSimilarityMax = 1;



export const SearchRequest = zod.object({
  "organizationId": zod.string().min(1),
  "deviceId": zod.union([zod.string(),zod.array(zod.string())]).optional(),
  "agentId": zod.string().optional(),
  "category": zod.string().optional().describe('Required with `searchText`, `image` or `imageEmbedding`: it selects the model.\n'),
  "utcTimeMs": zod.int().optional(),
  "utcEndTimeMs": zod.int().optional(),
  "attribute": zod.union([zod.string(),zod.array(zod.string())]).optional().describe('The attribute-mode filter of the GET form.'),
  "searchText": zod.string().optional(),
  "negativeText": zod.string().optional().describe('The `negativeText` of the GET form. Only `POST /events/search` takes it.'),
  "image": ImageInput.optional().describe('The query image of an image-similarity search.'),
  "imageEmbedding": zod.array(zod.number()).optional().describe('An image-space ReID vector to search by directly: the `imageEmbedding.data` that a listing returns `withEmbeddings`. Mutually exclusive with `image`.\n'),
  "identityId": zod.uuid().optional(),
  "boundingBox": zod.string().optional(),
  "region": zod.string().optional(),
  "limit": zod.int().min(1).max(searchRequestLimitMax).default(searchRequestLimitDefault),
  "offset": zod.int().min(searchRequestOffsetMin).default(searchRequestOffsetDefault),
  "withEmbeddings": zod.boolean().default(searchRequestWithEmbeddingsDefault),
  "minSimilarity": zod.number().min(searchRequestMinSimilarityMin).max(searchRequestMinSimilarityMax).optional(),
  "orderBy": OrderBy.optional()
}).describe('The body of `POST /events/search` and `POST /embeddings/search`. Field names are the query parameter names of the GET forms, except `organizationId`. `deviceId` and `attribute` take a comma-separated string or an array.\n');

export type SearchRequest = zod.input<typeof SearchRequest>;
export type SearchRequestOutput = zod.output<typeof SearchRequest>;

export const HeatmapRecord = zod.object({
  "organizationId": zod.string(),
  "deviceId": Guid,
  "utcTimeMs": zod.int(),
  "utcEndTimeMs": zod.int(),
  "category": zod.string(),
  "region": zod.string().optional().describe('The 32x56 motion bitmask (224 bytes), base64.')
});

export type HeatmapRecord = zod.input<typeof HeatmapRecord>;
export type HeatmapRecordOutput = zod.output<typeof HeatmapRecord>;





export const HeatmapInsert = zod.object({
  "organizationId": zod.string().min(1),
  "deviceId": zod.string().min(1),
  "utcTimeMs": zod.int().optional(),
  "utcEndTimeMs": zod.int().optional(),
  "category": zod.string().optional(),
  "region": RegionInput.optional()
}).describe('A heatmap record as media_server uploads it (`CloudHeatmapRecord`).');

export type HeatmapInsert = zod.input<typeof HeatmapInsert>;
export type HeatmapInsertOutput = zod.output<typeof HeatmapInsert>;

export const TimePeriod = zod.object({
  "start": zod.int(),
  "duration": zod.int()
});

export type TimePeriod = zod.input<typeof TimePeriod>;
export type TimePeriodOutput = zod.output<typeof TimePeriod>;


export const reidentifyRequestDryRunDefault = false;
export const reidentifyRequestKeepStaleDefault = false;

export const ReidentifyRequest = zod.object({
  "organizationId": zod.string().min(1),
  "from": zod.iso.date().optional().describe('First day of the scan, inclusive. Absent scans from the beginning.'),
  "to": zod.iso.date().optional().describe('Last day of the scan, inclusive. Absent scans up to now.'),
  "dryRun": zod.boolean().default(reidentifyRequestDryRunDefault).describe('Report what would change and write nothing.'),
  "keepStale": zod.boolean().default(reidentifyRequestKeepStaleDefault).describe('Do not clear the old verdicts first; the run only adds verdicts.')
});

export type ReidentifyRequest = zod.input<typeof ReidentifyRequest>;
export type ReidentifyRequestOutput = zod.output<typeof ReidentifyRequest>;

export const ReidentifyIdentity = zod.object({
  "enrollmentId": Guid,
  "name": zod.string(),
  "events": zod.int().describe('The events matched to the identity.')
});

export type ReidentifyIdentity = zod.input<typeof ReidentifyIdentity>;
export type ReidentifyIdentityOutput = zod.output<typeof ReidentifyIdentity>;

export const ReidentifyResult = zod.object({
  "scanned": zod.int(),
  "matched": zod.int(),
  "skippedNoVector": zod.int(),
  "unmatched": zod.int(),
  "applied": zod.boolean().describe('False for a dry run.'),
  "clearedPartitions": zod.int(),
  "identities": zod.array(ReidentifyIdentity)
});

export type ReidentifyResult = zod.input<typeof ReidentifyResult>;
export type ReidentifyResultOutput = zod.output<typeof ReidentifyResult>;


export const reembedRequestDryRunDefault = false;

export const ReembedRequest = zod.object({
  "organizationId": zod.string().min(1),
  "categoryClass": zod.string().optional().describe('The ReID class. Defaults to `object.person`.'),
  "from": zod.iso.date().optional().describe('First day of the scan, inclusive. Absent scans from the beginning.'),
  "to": zod.iso.date().optional().describe('Last day of the scan, inclusive. Absent scans up to now.'),
  "dryRun": zod.boolean().default(reembedRequestDryRunDefault).describe('Count what would be re-embedded; call nothing and write nothing.')
});

export type ReembedRequest = zod.input<typeof ReembedRequest>;
export type ReembedRequestOutput = zod.output<typeof ReembedRequest>;

export const ReembedResult = zod.object({
  "scanned": zod.int(),
  "withImage": zod.int().describe('The scanned rows that have a best shot, so can be re-embedded.'),
  "reEmbedded": zod.int(),
  "skippedNoImage": zod.int(),
  "skippedNoResult": zod.int().describe('The vectorization service returned no image-space vector.'),
  "applied": zod.boolean()
});

export type ReembedResult = zod.input<typeof ReembedResult>;
export type ReembedResultOutput = zod.output<typeof ReembedResult>;
/**
 * The ClickHouse server version and the type that cosineDistance returns over Array(Float32). It exposes no data, and it needs no credential, so operators can read it during an incident. It sits under `/metadata/api/v1`, because the ingress forwards only that prefix.
 * @summary ClickHouse facts for incident triage
 */
export const GetDiagnosticsResponse = zod.record(zod.string(), zod.string())


/**
 * Inserts a batch of events. The organization of each record must be the caller's.
 *
 * A client-supplied embedding whose model the vectorization service does not advertise,
 * or whose length is not the model's dim, is cleared and logged; the insert continues.
 * When the service runs with `--enrich-images`, missing embeddings are computed through
 * the vectorization service, best-effort.
 * @summary Batch insert events
 */
export const InsertEventsBodyItem = EventInsert
export const InsertEventsBody = zod.array(InsertEventsBodyItem).min(1)

export const InsertEventsResponse = InsertResult


/**
 * Purges matching records from every per-event table: the events fan-out (events, events_by_device, the hourly rollups, numeric attributes), all four embedding tables, and the image table. Runs synchronously: the call returns once the rows are deleted, so it can block on wide ranges. `DELETE /heatmap` purges motion data.
 * @summary Delete events by filters
 */

export const deleteEventsQueryDeviceIdItemRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(,[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})*$');


export const DeleteEventsQueryParams = zod.object({
  "orgId": zod.string().min(1).describe('The organization. The caller must be a member of it.'),
  "utcTimeMs": zod.int().optional().describe('Delete the records with utc_time_ms >= this value.'),
  "utcEndTimeMs": zod.int().optional().describe('Delete the records with utc_time_ms <= this value.'),
  "deviceId": zod.array(zod.string().regex(deleteEventsQueryDeviceIdItemRegExp)).optional().describe('Camera ids, 8-4-4-4-12 hex: repeat the parameter (`deviceId=a&deviceId=b`) or give a comma-separated list (`deviceId=a,b`). media_server ids carry no RFC 9562 version digit, so only the form is checked.\n')
})

export const DeleteEventsResponse = Status


/**
 * Unified search endpoint. The **ranking signal** that the caller provides selects the
 * mode:
 *
 * 1. **Attribute search**: no ranking signal. Structured filtering on category,
 *    attributes, time and region, newest first.
 * 2. **Text-similarity search**: `searchText`. The server vectorizes the text with the
 *    text model that `category` selects and ranks events by cosine similarity over
 *    `text_embedding`.
 * 3. **Image-similarity search**: `image` or `imageEmbedding`, POST only, because
 *    neither an image nor a full vector fits in a URL. `imageEmbedding` is an
 *    image-space vector the caller already holds (the value a listing returns
 *    `withEmbeddings`), used as the query directly.
 *
 * More than one ranking signal is a 400. `attribute` is valid only in attribute mode:
 * the embedding tables carry no attribute columns.
 *
 * `negativeText` refines a text-similarity search: a hit sinks in the order of
 * `searchText` by 0.3 times its similarity to `negativeText`. It applies only to
 * `searchText` in the similarity order.
 *
 * The other filters apply in every mode. A spatial filter on a vector search disables the
 * ANN index (exact scan under a row budget), so a window that is too wide is a 400 that
 * asks to narrow the time range.
 * @summary Search events (attribute or text similarity)
 */

export const searchEventsGetQueryDeviceIdItemRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(,[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})*$');
export const searchEventsGetQueryLimitDefault = 100;
export const searchEventsGetQueryLimitMax = 1000;

export const searchEventsGetQueryOffsetDefault = 0;
export const searchEventsGetQueryOffsetMin = 0;

export const searchEventsGetQueryWithEmbeddingsDefault = false;
export const searchEventsGetQueryMinSimilarityMin = 0;
export const searchEventsGetQueryMinSimilarityMax = 1;



export const SearchEventsGetQueryParams = zod.object({
  "orgId": zod.string().min(1).describe('The organization. The caller must be a member of it.'),
  "deviceId": zod.array(zod.string().regex(searchEventsGetQueryDeviceIdItemRegExp)).optional().describe('Camera ids, 8-4-4-4-12 hex: repeat the parameter (`deviceId=a&deviceId=b`) or give a comma-separated list (`deviceId=a,b`). media_server ids carry no RFC 9562 version digit, so only the form is checked.\n'),
  "agentId": zod.string().optional().describe('The analytics agent that produced the records.'),
  "category": zod.string().optional().describe('Category prefix match (`object` matches `object.person`). A vector search also selects its model with it.\n'),
  "utcTimeMs": zod.int().optional().describe('Start time, ms since epoch.'),
  "utcEndTimeMs": zod.int().optional().describe('End time, ms since epoch.'),
  "attribute": zod.array(zod.string()).optional().describe('Attribute-mode filter: `name<op>value`. Repeat the parameter, or give a\ncomma-separated list, to combine the filters with AND.\n- string: `color=red` (name prefix, value substring);\n- exclusion: `color!=red` also returns the events with no `color` at all\n  (`color!=` alone returns exactly those);\n- numeric: `speed>5`, `speed<=10`, `speed=[3..7]`, for an attribute that the agent\n  manifests of the organization declare `number` or `integer`. On another attribute an\n  ordering or a range is a 400, and `=` compares strings.\n\nMutually exclusive with `searchText`, `image` and `imageEmbedding`.\n'),
  "searchText": zod.string().optional().describe('Text-similarity query in natural language. Requires `category`, which selects the text model. Mutually exclusive with `attribute`, `image` and `imageEmbedding`.\n'),
  "negativeText": zod.string().optional().describe('What the results must not look like, in natural language ("bicycle"). Requires `searchText`, and the similarity order. The server searches with the vector `normalize(q - 0.3 * n)`, `q` the embedding of `searchText` and `n` the embedding of `negativeText`, so the `similarity` of a hit is its similarity to `searchText` minus 0.3 times its similarity to `negativeText`, over a constant. `minSimilarity` applies to that `similarity`. Write the positive query without the negation: a `searchText` that names the excluded thing finds only that thing.\n'),
  "identityId": zod.uuid().optional().describe('Only the events whose stored enrolled-identity verdict names this identity: an indexed column, not a vector search. Applies to attribute search.\n'),
  "boundingBox": zod.string().optional().describe('Rectangle in normalized [0..1] coordinates, `x,y,w,h` or `x,y,wxh`. The server rasterizes it onto the 32x56 region grid and returns only the records whose stored region shares a cell with it. Mutually exclusive with `region`.\n'),
  "region": zod.string().optional().describe('A 32x56 bitmask (224 bytes) in standard base64. Returns only the records whose stored region shares a set bit with it. An unescaped `+` arrives as a space, and the server reads a space as `+`. Mutually exclusive with `boundingBox`.\n'),
  "limit": zod.int().min(1).max(searchEventsGetQueryLimitMax).default(searchEventsGetQueryLimitDefault).describe('Page size. A full page of 1000 rows means that there can be more: page on with `offset` or a narrower time range.\n'),
  "offset": zod.int().min(searchEventsGetQueryOffsetMin).default(searchEventsGetQueryOffsetDefault).describe('Rows to skip.'),
  "withEmbeddings": zod.boolean().default(searchEventsGetQueryWithEmbeddingsDefault).describe('Include the embedding vectors of each hit (`textEmbedding` / `imageEmbedding`). The vectors are large.\n'),
  "minSimilarity": zod.number().min(searchEventsGetQueryMinSimilarityMin).max(searchEventsGetQueryMinSimilarityMax).optional().describe('Cosine-similarity floor of a vector search: hits below it are dropped. Defaults to 0.05, which removes the long tail of near-orthogonal matches. `0` returns the raw ranked candidate set. Attribute search ignores it.\n'),
  "orderBy": zod.enum(['similarity', 'startTimeMs']).optional().describe('Result order. `similarity` is the default of a vector search and needs a ranking signal. `startTimeMs` is newest first: the only order of attribute search, and on a vector search a row-budgeted brute-force scan. The order changes only which page of the result set `limit` exposes, never which rows qualify.\n')
})

export const SearchEventsGetResponseItem = EventSearchResult
export const SearchEventsGetResponse = zod.array(SearchEventsGetResponseItem)


/**
 * The parameters of `GET /events/search` in a JSON body, plus `image` and
 * `imageEmbedding`, which select image-similarity mode. The mode rules and validation are
 * those of the GET form.
 * @summary Search events (any mode, including image similarity)
 */
export const SearchEventsPostBody = SearchRequest

export const SearchEventsPostResponseItem = EventSearchResult
export const SearchEventsPostResponse = zod.array(SearchEventsPostResponseItem)


/**
 * JOIN-free counterpart of `GET /events/search`. Ranks rows by cosine similarity over the
 * embedding tables and returns only the columns of the embedding row, so it also returns
 * rows that have only an embedding (for example `event.sceneEmbedding`), which
 * `/events/search` does not.
 *
 * Requires a vector signal: `searchText`, or `image` (POST only). Attribute mode and
 * `identityId` are only in `/events/search`.
 * @summary Lean vector search over the embedding tables
 */

export const searchEmbeddingsGetQueryDeviceIdItemRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(,[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})*$');
export const searchEmbeddingsGetQueryLimitDefault = 100;
export const searchEmbeddingsGetQueryLimitMax = 1000;

export const searchEmbeddingsGetQueryWithEmbeddingsDefault = false;
export const searchEmbeddingsGetQueryMinSimilarityMin = 0;
export const searchEmbeddingsGetQueryMinSimilarityMax = 1;



export const SearchEmbeddingsGetQueryParams = zod.object({
  "orgId": zod.string().min(1).describe('The organization. The caller must be a member of it.'),
  "deviceId": zod.array(zod.string().regex(searchEmbeddingsGetQueryDeviceIdItemRegExp)).optional().describe('Camera ids, 8-4-4-4-12 hex: repeat the parameter (`deviceId=a&deviceId=b`) or give a comma-separated list (`deviceId=a,b`). media_server ids carry no RFC 9562 version digit, so only the form is checked.\n'),
  "category": zod.string().optional().describe('Category prefix match (`object` matches `object.person`). A vector search also selects its model with it.\n'),
  "utcTimeMs": zod.int().optional().describe('Start time, ms since epoch.'),
  "utcEndTimeMs": zod.int().optional().describe('End time, ms since epoch.'),
  "searchText": zod.string().optional().describe('Text-similarity query in natural language. Requires `category`, which selects the text model. Mutually exclusive with `attribute`, `image` and `imageEmbedding`.\n'),
  "boundingBox": zod.string().optional().describe('Rectangle in normalized [0..1] coordinates, `x,y,w,h` or `x,y,wxh`. The server rasterizes it onto the 32x56 region grid and returns only the records whose stored region shares a cell with it. Mutually exclusive with `region`.\n'),
  "region": zod.string().optional().describe('A 32x56 bitmask (224 bytes) in standard base64. Returns only the records whose stored region shares a set bit with it. An unescaped `+` arrives as a space, and the server reads a space as `+`. Mutually exclusive with `boundingBox`.\n'),
  "limit": zod.int().min(1).max(searchEmbeddingsGetQueryLimitMax).default(searchEmbeddingsGetQueryLimitDefault).describe('Page size. A full page of 1000 rows means that there can be more: page on with `offset` or a narrower time range.\n'),
  "withEmbeddings": zod.boolean().default(searchEmbeddingsGetQueryWithEmbeddingsDefault).describe('Include the embedding vectors of each hit (`textEmbedding` / `imageEmbedding`). The vectors are large.\n'),
  "minSimilarity": zod.number().min(searchEmbeddingsGetQueryMinSimilarityMin).max(searchEmbeddingsGetQueryMinSimilarityMax).optional().describe('Cosine-similarity floor of a vector search: hits below it are dropped. Defaults to 0.05, which removes the long tail of near-orthogonal matches. `0` returns the raw ranked candidate set. Attribute search ignores it.\n'),
  "orderBy": zod.enum(['similarity', 'startTimeMs']).optional().describe('Result order. `similarity` is the default of a vector search and needs a ranking signal. `startTimeMs` is newest first: the only order of attribute search, and on a vector search a row-budgeted brute-force scan. The order changes only which page of the result set `limit` exposes, never which rows qualify.\n')
})

export const SearchEmbeddingsGetResponseItem = EmbeddingHit
export const SearchEmbeddingsGetResponse = zod.array(SearchEmbeddingsGetResponseItem)


/**
 * `GET /embeddings/search` with the request in a JSON body, plus `image` for image-similarity search.
 * @summary Lean vector search (any mode, including image similarity)
 */
export const SearchEmbeddingsPostBody = SearchRequest

export const SearchEmbeddingsPostResponseItem = EmbeddingHit
export const SearchEmbeddingsPostResponse = zod.array(SearchEmbeddingsPostResponseItem)


/**
 * @summary Query aggregated time periods for events
 */

export const queryEventSegmentsQueryDeviceIdItemRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(,[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})*$');

export const queryEventSegmentsQueryResultInIntervalsDefault = false;
export const queryEventSegmentsQueryFlatDefault = true;

export const QueryEventSegmentsQueryParams = zod.object({
  "orgId": zod.string().min(1).describe('The organization. The caller must be a member of it.'),
  "deviceId": zod.array(zod.string().regex(queryEventSegmentsQueryDeviceIdItemRegExp)).optional().describe('Camera ids, 8-4-4-4-12 hex: repeat the parameter (`deviceId=a&deviceId=b`) or give a comma-separated list (`deviceId=a,b`). media_server ids carry no RFC 9562 version digit, so only the form is checked.\n'),
  "agentId": zod.string().optional().describe('The analytics agent that produced the records.'),
  "category": zod.string().optional().describe('Category prefix match (`object` matches `object.person`). A vector search also selects its model with it.\n'),
  "utcTimeMs": zod.int().optional().describe('Start time, ms since epoch.'),
  "utcEndTimeMs": zod.int().optional().describe('End time, ms since epoch.'),
  "attribute": zod.array(zod.string()).optional().describe('Attribute-mode filter: `name<op>value`. Repeat the parameter, or give a\ncomma-separated list, to combine the filters with AND.\n- string: `color=red` (name prefix, value substring);\n- exclusion: `color!=red` also returns the events with no `color` at all\n  (`color!=` alone returns exactly those);\n- numeric: `speed>5`, `speed<=10`, `speed=[3..7]`, for an attribute that the agent\n  manifests of the organization declare `number` or `integer`. On another attribute an\n  ordering or a range is a 400, and `=` compares strings.\n\nMutually exclusive with `searchText`, `image` and `imageEmbedding`.\n'),
  "boundingBox": zod.string().optional().describe('Rectangle in normalized [0..1] coordinates, `x,y,w,h` or `x,y,wxh`. The server rasterizes it onto the 32x56 region grid and returns only the records whose stored region shares a cell with it. Mutually exclusive with `region`.\n'),
  "region": zod.string().optional().describe('A 32x56 bitmask (224 bytes) in standard base64. Returns only the records whose stored region shares a set bit with it. An unescaped `+` arrives as a space, and the server reads a space as `+`. Mutually exclusive with `boundingBox`.\n'),
  "interval": zod.int().min(1).optional().describe('Aggregation interval, ms. Defaults to 5000.'),
  "resultInIntervals": zod.boolean().default(queryEventSegmentsQueryResultInIntervalsDefault).describe('Give the values in interval units, not in ms.'),
  "flat": zod.boolean().default(queryEventSegmentsQueryFlatDefault).describe('Give a delta-encoded flat array (`[start1, duration1, gap1, duration2, ...]`), not period objects.\n')
})

export const QueryEventSegmentsResponse = zod.union([zod.object({
  "periods": zod.array(TimePeriod)
}).describe('Period objects (`flat=false`).'),zod.array(zod.int()).describe('Delta-encoded flat array (`flat=true`).')])


/**
 * @summary Get event by id
 */
export const GetEventByIdParams = zod.object({
  "id": zod.uuid().describe('The event id.')
})




export const GetEventByIdQueryParams = zod.object({
  "orgId": zod.string().min(1).describe('The organization. The caller must be a member of it.')
})

export const GetEventByIdResponse = EventMetadata


/**
 * Stores the request body as the event's image of `imageType`. The `Content-Type` of the request is stored as the image's MIME type.
 * @summary Upload an image for an event
 */
export const UploadEventImageParams = zod.object({
  "id": zod.uuid().describe('The event id.')
})


export const uploadEventImageQueryImageTypeDefault = `best_shot`;

export const UploadEventImageQueryParams = zod.object({
  "orgId": zod.string().min(1).describe('The organization. The caller must be a member of it.'),
  "imageType": zod.string().default(uploadEventImageQueryImageTypeDefault).describe('The stored image type.')
})

export const UploadEventImageResponse = zod.object({
  "id": zod.uuid()
})


/**
 * Returns the event's best shot as a direct blob. Other image types are requested explicitly with `imageType`. A caller whose role the organization's privacy setting restricts gets a blurred rendering of a restricted category.
 * @summary Get the event's stored image
 */
export const GetEventImageParams = zod.object({
  "id": zod.uuid().describe('The event id.')
})


export const getEventImageQueryImageTypeDefault = `best_shot`;

export const GetEventImageQueryParams = zod.object({
  "orgId": zod.string().min(1).describe('The organization. The caller must be a member of it.'),
  "imageType": zod.string().default(getEventImageQueryImageTypeDefault).describe('The stored image type.')
})

export const GetEventImageResponse = zod.unknown()


/**
 * Inserts a batch of records. The organization of each must be the caller's.
 * @summary Batch insert heatmap records
 */
export const InsertHeatmapBodyItem = HeatmapInsert
export const InsertHeatmapBody = zod.array(InsertHeatmapBodyItem).min(1)

export const InsertHeatmapResponse = InsertResult


/**
 * @summary Query heatmap records with filters
 */

export const queryHeatmapQueryDeviceIdItemRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(,[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})*$');
export const queryHeatmapQueryLimitDefault = 100;
export const queryHeatmapQueryLimitMax = 1000;

export const queryHeatmapQueryOffsetDefault = 0;
export const queryHeatmapQueryOffsetMin = 0;



export const QueryHeatmapQueryParams = zod.object({
  "orgId": zod.string().min(1).describe('The organization. The caller must be a member of it.'),
  "deviceId": zod.array(zod.string().regex(queryHeatmapQueryDeviceIdItemRegExp)).optional().describe('Camera ids, 8-4-4-4-12 hex: repeat the parameter (`deviceId=a&deviceId=b`) or give a comma-separated list (`deviceId=a,b`). media_server ids carry no RFC 9562 version digit, so only the form is checked.\n'),
  "category": zod.string().optional().describe('Category prefix match (`object` matches `object.person`). A vector search also selects its model with it.\n'),
  "utcTimeMs": zod.int().optional().describe('Start time, ms since epoch.'),
  "utcEndTimeMs": zod.int().optional().describe('End time, ms since epoch.'),
  "boundingBox": zod.string().optional().describe('Rectangle in normalized [0..1] coordinates, `x,y,w,h` or `x,y,wxh`. The server rasterizes it onto the 32x56 region grid and returns only the records whose stored region shares a cell with it. Mutually exclusive with `region`.\n'),
  "region": zod.string().optional().describe('A 32x56 bitmask (224 bytes) in standard base64. Returns only the records whose stored region shares a set bit with it. An unescaped `+` arrives as a space, and the server reads a space as `+`. Mutually exclusive with `boundingBox`.\n'),
  "limit": zod.int().min(1).max(queryHeatmapQueryLimitMax).default(queryHeatmapQueryLimitDefault).describe('Page size. A full page of 1000 rows means that there can be more: page on with `offset` or a narrower time range.\n'),
  "offset": zod.int().min(queryHeatmapQueryOffsetMin).default(queryHeatmapQueryOffsetDefault).describe('Rows to skip.')
})

export const QueryHeatmapResponseItem = HeatmapRecord
export const QueryHeatmapResponse = zod.array(QueryHeatmapResponseItem)


/**
 * Purges motion data from the raw `heatmap` table and the `heatmap_hourly` rollup. Runs synchronously.
 * @summary Delete heatmap records by filters
 */

export const deleteHeatmapQueryDeviceIdItemRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(,[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})*$');


export const DeleteHeatmapQueryParams = zod.object({
  "orgId": zod.string().min(1).describe('The organization. The caller must be a member of it.'),
  "utcTimeMs": zod.int().optional().describe('Delete the records with utc_time_ms >= this value.'),
  "utcEndTimeMs": zod.int().optional().describe('Delete the records with utc_time_ms <= this value.'),
  "deviceId": zod.array(zod.string().regex(deleteHeatmapQueryDeviceIdItemRegExp)).optional().describe('Camera ids, 8-4-4-4-12 hex: repeat the parameter (`deviceId=a&deviceId=b`) or give a comma-separated list (`deviceId=a,b`). media_server ids carry no RFC 9562 version digit, so only the form is checked.\n')
})

export const DeleteHeatmapResponse = Status


/**
 * @summary Query aggregated time periods for heatmap
 */

export const queryHeatmapSegmentsQueryDeviceIdItemRegExp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(,[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})*$');

export const queryHeatmapSegmentsQueryResultInIntervalsDefault = false;
export const queryHeatmapSegmentsQueryFlatDefault = true;

export const QueryHeatmapSegmentsQueryParams = zod.object({
  "orgId": zod.string().min(1).describe('The organization. The caller must be a member of it.'),
  "deviceId": zod.array(zod.string().regex(queryHeatmapSegmentsQueryDeviceIdItemRegExp)).optional().describe('Camera ids, 8-4-4-4-12 hex: repeat the parameter (`deviceId=a&deviceId=b`) or give a comma-separated list (`deviceId=a,b`). media_server ids carry no RFC 9562 version digit, so only the form is checked.\n'),
  "category": zod.string().optional().describe('Category prefix match (`object` matches `object.person`). A vector search also selects its model with it.\n'),
  "utcTimeMs": zod.int().optional().describe('Start time, ms since epoch.'),
  "utcEndTimeMs": zod.int().optional().describe('End time, ms since epoch.'),
  "boundingBox": zod.string().optional().describe('Rectangle in normalized [0..1] coordinates, `x,y,w,h` or `x,y,wxh`. The server rasterizes it onto the 32x56 region grid and returns only the records whose stored region shares a cell with it. Mutually exclusive with `region`.\n'),
  "region": zod.string().optional().describe('A 32x56 bitmask (224 bytes) in standard base64. Returns only the records whose stored region shares a set bit with it. An unescaped `+` arrives as a space, and the server reads a space as `+`. Mutually exclusive with `boundingBox`.\n'),
  "interval": zod.int().min(1).optional().describe('Aggregation interval, ms. Defaults to 5000.'),
  "resultInIntervals": zod.boolean().default(queryHeatmapSegmentsQueryResultInIntervalsDefault).describe('Give the values in interval units, not in ms.'),
  "flat": zod.boolean().default(queryHeatmapSegmentsQueryFlatDefault).describe('Give a delta-encoded flat array (`[start1, duration1, gap1, duration2, ...]`), not period objects.\n')
})

export const QueryHeatmapSegmentsResponse = zod.union([zod.object({
  "periods": zod.array(TimePeriod)
}).describe('Period objects (`flat=false`).'),zod.array(zod.int()).describe('Delta-encoded flat array (`flat=true`).')])


/**
 * Reads the organization's enrollment galleries and thresholds from vms_database, matches
 * each stored object with a ReID vector against them, and rewrites the verdicts
 * (`enrollmentId`, `enrollmentSimilarity`) on the events. The organization's own
 * thresholds apply; when vms_database cannot give them, the service defaults apply.
 *
 * Without `keepStale`, the run first clears the old verdicts in the window, so a verdict
 * that no longer matches is removed. One run per organization at a time.
 *
 * Only an administrator of the organization can start a run or a `dryRun`.
 * @summary Match the stored objects to the enrolled identities again
 */
export const ReidentifyEnrollmentsBody = ReidentifyRequest

export const ReidentifyEnrollmentsResponse = ReidentifyResult


/**
 * Recomputes the stored ReID image vectors from their best-shot images through the
 * vectorization service, so older events sit in the space of the current model. Run it
 * before `POST /enrollments/reidentify` after a model change.
 *
 * A `dryRun` scans and returns its counts. A real run takes too long for one request: it
 * runs in the background, one per organization at a time, and writes its result to the
 * service log.
 *
 * Only an administrator of the organization can start a run or a `dryRun`.
 * @summary Recompute the stored ReID image vectors
 */
export const ReembedImagesBody = ReembedRequest

export const ReembedImagesResponse = ReembedResult


/**
 * Proxy to the text endpoint of the vectorization service. It returns embeddings in the space that event ingest uses, so a caller can compare a query string with the stored text embeddings. It reads no organization data.
 * @summary Vectorize text
 */



export const VectorizeTextBody = zod.object({
  "texts": zod.array(zod.string()).min(1),
  "category": zod.string().optional().describe('Selects the text model by category class: `event` for the event text model, `object` (or an `object.*` category) for the object text tower. Empty lets the vectorization service pick its default.\n')
})

export const VectorizeTextResponse = zod.object({
  "model": zod.string(),
  "dim": zod.int(),
  "embeddings": zod.array(zod.array(zod.number()))
})
